import { useState, useEffect } from "react";
import { useAuth, useUser } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import { IssuesTable, type IssueRecord } from "../components/admin/IssuesTable";
import { TopBar } from "../components/TopBar";
import styles from "../styles/pages/AdminSubPage.module.css";

export function AdminIssuesPage() {
  const { isLoaded, userId } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const isAdmin = role === "admin" || role === "editor";

  useEffect(() => {
    if (isLoaded && (!userId || !isAdmin)) {
      navigate({ to: "/" });
    }
  }, [isLoaded, userId, isAdmin, navigate]);

  const [issues, setIssues] = useState<IssueRecord[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isLoaded && userId) load();
  }, [isLoaded, userId]);

  async function load() {
    setLoading(true);
    try {
      const data = await fetch("/api/issues").then((r) => r.json());
      setIssues(data.issues);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(sourceId: string) {
    setDeleting((prev) => new Set(prev).add(sourceId));
    try {
      await fetch(`/api/issues/${sourceId}`, { method: "DELETE" });
      setIssues((prev) => prev?.filter((r) => r.sourceId !== sourceId) ?? null);
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(sourceId);
        return next;
      });
    }
  }

  if (!isLoaded || !userId || !isAdmin) return null;

  return (
    <div className={styles.page}>
      <TopBar />
      <div className={styles.content}>
        <div className={styles.header}>
          <Link to="/admin" className={styles.back}>← Admin</Link>
          <h1 className={styles.title}>Reported Issues</h1>
        </div>
        <IssuesTable
          issues={issues}
          loading={loading}
          deleting={deleting}
          onLoad={load}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}
